import React, { useState, useEffect } from 'react'
import { Comment } from '../types'
import { useForm } from 'react-hook-form'
import { API_BASE_URL } from '../config'

interface CommentSectionProps {
  albumId: number
}

interface CommentFormData {
  author: string
  content: string
}

const CommentSection: React.FC<CommentSectionProps> = ({ albumId }) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CommentFormData>()

  // 加载评论
  useEffect(() => {
    fetchComments()
  }, [albumId])

  const fetchComments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/albums/${albumId}/comments`)
      const data = await response.json()
      setComments(data)
    } catch (error) {
      console.error('Failed to fetch comments:', error)
    }
  }

  // 提交评论
  const onSubmit = async (data: CommentFormData) => {
    setIsLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/albums/${albumId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        reset()
        fetchComments() // 重新加载评论
      }
    } catch (error) {
      console.error('Failed to post comment:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mt-8 bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">评论 ({comments.length})</h3>

      {/* 评论表单 */}
      <form onSubmit={handleSubmit(onSubmit)} className="mb-6 space-y-4">
        <div>
          <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
            昵称
          </label>
          <input
            id="author"
            type="text"
            {...register('author', { required: '请输入昵称' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="你的昵称"
          />
          {errors.author && (
            <p className="mt-1 text-sm text-red-600">{errors.author.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
            评论内容
          </label>
          <textarea
            id="content"
            {...register('content', { required: '请输入评论内容' })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="写下你的评论..."
          />
          {errors.content && (
            <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? '提交中...' : '发表评论'}
        </button>
      </form>

      {/* 评论列表 */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-center py-4">暂无评论，快来发表第一条评论吧！</p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-200 pb-4 last:border-0">
              <div className="flex items-start justify-between mb-2">
                <span className="font-medium text-gray-900">{comment.author}</span>
                <span className="text-sm text-gray-500">
                  {new Date(comment.created_at).toLocaleDateString('zh-CN')}
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default CommentSection
